import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, MessageSquare, UserCheck, TrendingUp, Globe2, Loader2, AlertCircle, Send, ThumbsDown, Brain, Tag, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { getAnalytics, getConversation, getDigest, sendDigestNow, listDeclineReasons, getDigestInsights } from "@/lib/api";

const LANG_LABEL = { tr: "Türkçe", en: "English", ar: "العربية", ru: "Русский" };

function StatCard({ icon: Icon, label, value, sub, color = "#B39B7A", testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      className="bg-white border border-border/40 rounded-2xl p-5"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">{label}</div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}22`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-serif text-4xl mt-3" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </motion.div>
  );
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [digest, setDigestData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [declines, setDeclines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, d, de] = await Promise.all([getAnalytics(), getDigest(), listDeclineReasons()]);
      setData(a); setDigestData(d); setDeclines(de);
    } finally { setLoading(false); }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const i = await getDigestInsights();
      setInsights(i);
    } catch (e) {
      toast.error("AI özet hazırlanamadı");
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (data) loadInsights(); /* eslint-disable-line */ }, [data]);

  // Defensive: ensure all numeric fields used by the UI exist, even if backend returns partial data
  const safe = data ? {
    total_visits: data.total_visits ?? 0,
    today_visits: data.today_visits ?? 0,
    last_7_days_visits: data.last_7_days_visits ?? 0,
    total_conversations: data.total_conversations ?? 0,
    total_leads: data.total_leads ?? 0,
    converted_sessions: data.converted_sessions ?? 0,
    conversion_rate: data.conversion_rate ?? 0,
    daily_chart: Array.isArray(data.daily_chart) ? data.daily_chart : [],
    lang_breakdown: Array.isArray(data.lang_breakdown) ? data.lang_breakdown : [],
    country_breakdown: Array.isArray(data.country_breakdown) ? data.country_breakdown : [],
    topics: Array.isArray(data.topics) ? data.topics : [],
    recent_sessions: Array.isArray(data.recent_sessions) ? data.recent_sessions : [],
    abandoned: Array.isArray(data.abandoned) ? data.abandoned : [],
    abandoned_count: data.abandoned_count ?? 0,
  } : null;

  const openConvo = async (sid) => {
    const msgs = await getConversation(sid);
    setView({ sid, msgs });
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await sendDigestNow();
      if (r.delivery?.ok) toast.success("Günlük özet WhatsApp'a gönderildi");
      else toast.error("Gönderim başarısız: " + (r.delivery?.error || "Twilio kurulu değil"));
      load();
    } finally { setSending(false); }
  };

  if (loading || !data || !safe) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label="Toplam Ziyaret"
          value={safe.total_visits.toLocaleString("tr-TR")}
          sub={`Bugün: ${safe.today_visits} · Son 7 gün: ${safe.last_7_days_visits}`}
          color="#B39B7A"
          testId="stat-visits"
        />
        <StatCard
          icon={MessageSquare}
          label="Konuşma"
          value={safe.total_conversations.toLocaleString("tr-TR")}
          sub={`${safe.total_visits ? Math.round((safe.total_conversations / safe.total_visits) * 100) : 0}% ziyaretçi sohbet açtı`}
          color="#8B9B88"
          testId="stat-conversations"
        />
        <StatCard
          icon={UserCheck}
          label="Lead'e Dönüşen"
          value={safe.total_leads.toLocaleString("tr-TR")}
          sub={`${safe.converted_sessions} sohbet iletişim bıraktı`}
          color="#2C3531"
          testId="stat-leads"
        />
        <StatCard
          icon={TrendingUp}
          label="Dönüşüm Oranı"
          value={`%${safe.conversion_rate}`}
          sub="sohbet → lead"
          color="#B39B7A"
          testId="stat-conversion"
        />
      </div>

      {/* Daily chart */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Son 14 Gün — Günlük Ziyaret</CardTitle>
        </CardHeader>
        <CardContent>
          {!Array.isArray(safe.daily_chart) || safe.daily_chart.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">Henüz görüntülenecek veri yok</div>
          ) : (
            <div className="w-full" style={{ height: 256, minHeight: 256 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={safe.daily_chart} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#E5E0D8" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#5E6B62" }}
                    tickFormatter={(d) => (d || "").slice(5)}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#5E6B62" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "white", border: "1px solid #E5E0D8", borderRadius: 12 }}
                    labelStyle={{ color: "#2C3531", fontFamily: "Outfit" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="visits"
                    stroke="#B39B7A"
                    strokeWidth={2.5}
                    dot={{ fill: "#B39B7A", r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lang + country */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><Globe2 className="w-4 h-4" /> Dil Dağılımı</CardTitle></CardHeader>
          <CardContent>
            {!Array.isArray(safe.lang_breakdown) || safe.lang_breakdown.length === 0 ? <div className="text-sm text-muted-foreground">Henüz veri yok</div> :
              <div className="w-full" style={{ height: 208, minHeight: 208 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={safe.lang_breakdown.map((l) => ({ name: LANG_LABEL[l.language] || l.language, count: l.count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6B62" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#5E6B62" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E0D8", borderRadius: 12 }} />
                    <Bar dataKey="count" fill="#8B9B88" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            }
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-display">Ülke Dağılımı</CardTitle></CardHeader>
          <CardContent>
            {safe.country_breakdown.length === 0 ? <div className="text-sm text-muted-foreground">Henüz veri yok</div> :
              <div className="space-y-2">
                {safe.country_breakdown.map((c) => {
                  const max = Math.max(...safe.country_breakdown.map((x) => x.count));
                  const pct = (c.count / max) * 100;
                  return (
                    <div key={c.country} data-testid={`country-${c.country}`} className="flex items-center gap-3">
                      <div className="w-12 font-mono text-xs">{c.country}</div>
                      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#B39B7A" }} />
                      </div>
                      <div className="w-10 text-right text-sm font-medium">{c.count}</div>
                    </div>
                  );
                })}
              </div>
            }
          </CardContent>
        </Card>
      </div>

      {/* AI-Powered intelligent summary */}
      <Card data-testid="insights-card" className="border-primary/30 bg-gradient-to-br from-white to-secondary/30">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <Brain className="w-4 h-4" style={{ color: "#B39B7A" }} />
                Bugünün Akıllı Özeti
                {insights && <Badge variant="outline">{insights.date}</Badge>}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                AI gün boyunca gelen sorulari analiz eder, başlıkları gruplar ve sayfada cevabı olmayan konuları işaretler.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadInsights}
              disabled={insightsLoading}
              data-testid="insights-refresh"
            >
              {insightsLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Brain className="w-3 h-3 mr-2" />}
              Yenile
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {insightsLoading && !insights ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> AI günü analiz ediyor...
            </div>
          ) : insights ? (
            <div className="space-y-5">
              {/* Top numbers */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-border/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Toplam Soru</div>
                  <div className="font-serif text-3xl mt-1">{insights.total_questions}</div>
                  <div className="text-xs text-muted-foreground mt-1">{insights.unique_sessions} farklı kişi</div>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-border/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Olumlu</div>
                  <div className="font-serif text-3xl mt-1" style={{ color: "#8B9B88" }}>{insights.positive_outcomes}</div>
                  <div className="text-xs text-muted-foreground mt-1">iletişim bilgisi bıraktı</div>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-border/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Olumsuz</div>
                  <div className="font-serif text-3xl mt-1" style={{ color: "#B39B7A" }}>{insights.negative_outcomes.length}</div>
                  <div className="text-xs text-muted-foreground mt-1">reddetme</div>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-border/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sayfada Yok</div>
                  <div className="font-serif text-3xl mt-1" style={{ color: "#D97706" }}>{(insights.gap_topics || []).length}</div>
                  <div className="text-xs text-muted-foreground mt-1">eksik konu</div>
                </div>
              </div>

              {/* AI summary text */}
              {insights.summary && (
                <div className="bg-white rounded-2xl p-5 border border-border/40 relative">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display mb-2">Günün Özeti</div>
                  <p className="font-serif text-lg leading-relaxed">{insights.summary}</p>
                </div>
              )}

              {/* Topic clusters */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="font-display text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-3">
                    <Tag className="w-3 h-3" /> En Çok Sorulan Başlıklar
                  </div>
                  {(insights.topics || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground bg-secondary/40 rounded-xl p-4">Henüz veri yok</div>
                  ) : (
                    <div className="space-y-2">
                      {insights.topics.map((t, i) => (
                        <div key={i} data-testid={`topic-${i}`} className="bg-white rounded-xl p-3 border border-border/40">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm">{t.name}</div>
                            <Badge variant="secondary" className="text-[10px]">{t.count}</Badge>
                          </div>
                          {t.samples && t.samples.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <span className="italic">örn: "{t.samples[0]?.slice(0, 90)}{t.samples[0]?.length > 90 ? "…" : ""}"</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="font-display text-sm uppercase tracking-widest text-amber-700 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-3 h-3" /> Sayfada Olmayan Konular
                  </div>
                  {(insights.gap_topics || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground bg-secondary/40 rounded-xl p-4">🎉 Tüm sorular sayfada cevaplanabiliyor</div>
                  ) : (
                    <div className="space-y-2">
                      {insights.gap_topics.map((t, i) => (
                        <div key={i} data-testid={`gap-${i}`} className="bg-amber-50 rounded-xl p-3 border border-amber-200/60">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm text-amber-900">{t.name}</div>
                            <Badge style={{ background: "#D97706", color: "white" }} className="text-[10px]">{t.count}</Badge>
                          </div>
                          {t.samples && t.samples.length > 0 && (
                            <div className="mt-2 text-xs text-amber-800/80">
                              <span className="italic">"{t.samples[0]?.slice(0, 110)}{t.samples[0]?.length > 110 ? "…" : ""}"</span>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="text-xs text-muted-foreground bg-secondary/30 rounded-lg p-3 mt-2">
                        💡 Bu başlıklar için Bilgi Tabanı'na içerik eklemeyi düşünebilirsiniz.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">
              <Button onClick={loadInsights} variant="outline" size="sm">
                <Brain className="w-4 h-4 mr-2" /> Akıllı özeti hazırla
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily digest preview */}
      {digest && (
        <Card data-testid="digest-card" className="border-primary/30">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="font-display flex items-center gap-2">
                  <Send className="w-4 h-4" /> Bugünün WhatsApp Özeti
                  <Badge variant="outline" className="ml-1">{digest.date}</Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Bu özet her gün otomatik olarak WhatsApp'a gönderilir (Ayarlar'da saat ve durum yönetilir).
                </p>
              </div>
              <Button data-testid="digest-send-now" onClick={sendNow} disabled={sending} style={{ background: "#B39B7A" }}>
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Şimdi WhatsApp'a Yolla
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-secondary/40 rounded-xl p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ziyaret</div>
                <div className="font-serif text-2xl mt-1">{digest.visits}</div>
              </div>
              <div className="bg-secondary/40 rounded-xl p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sohbet</div>
                <div className="font-serif text-2xl mt-1">{digest.conversations}</div>
              </div>
              <div className="bg-secondary/40 rounded-xl p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lead</div>
                <div className="font-serif text-2xl mt-1" style={{ color: "#8B9B88" }}>{digest.leads.length}</div>
              </div>
              <div className="bg-secondary/40 rounded-xl p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Reddedenler</div>
                <div className="font-serif text-2xl mt-1" style={{ color: "#B39B7A" }}>{digest.declines.length}</div>
              </div>
              <div className="bg-secondary/40 rounded-xl p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Yarım kalan</div>
                <div className="font-serif text-2xl mt-1">{digest.abandoned}</div>
              </div>
            </div>
            {digest.declines.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-display uppercase tracking-widest text-muted-foreground">Bugünün reddetme sebepleri</div>
                {digest.declines.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-secondary/30 text-sm">
                    <ThumbsDown className="w-3 h-3 mt-1 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <div>{d.reason}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        <Badge variant="outline" className="text-[10px] mr-2">{(d.language || "tr").toUpperCase()}</Badge>
                        {new Date(d.ts).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All-time decline reasons */}
      {declines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <ThumbsDown className="w-4 h-4 text-amber-600" /> Tüm Reddetme Sebepleri
              <Badge variant="secondary" className="ml-1">{declines.length}</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              AI'nın iletişimi reddeden kullanıcılardan kibarca öğrendiği veya kullanıcının kendi yazdığı sebepler.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {declines.map((d) => (
                <div key={d.id} data-testid={`decline-${d.id}`} className="p-3 rounded-xl border border-border/40 flex items-start gap-3">
                  <ThumbsDown className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{d.reason}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <Badge variant="outline" className="text-[10px]">{(d.language || "tr").toUpperCase()}</Badge>
                      <Badge variant="outline" className="text-[10px]">{d.source}</Badge>
                      <span className="text-muted-foreground">{new Date(d.ts).toLocaleString()}</span>
                      <button onClick={() => openConvo(d.session_id)} className="text-primary hover:underline">Konuşmayı gör</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Abandoned conversations */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                Lead'e Dönüşmeyen Konuşmalar
                <Badge variant="secondary" className="ml-2">{safe.abandoned_count}</Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Bu kullanıcılar sohbet açtı ama iletişim bilgisi bırakmadı. AI'ya hangi sorulara yanıt veremediğini analiz etmek için faydalıdır.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {safe.abandoned.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8">
              🎉 Tüm konuşmalar lead'e dönüştü ya da henüz konuşma yok.
            </div>
          ) : (
            <div className="space-y-2">
              {safe.abandoned.map((a) => (
                <div
                  key={a.session_id}
                  data-testid={`abandoned-${a.session_id}`}
                  className="p-4 border border-border/40 rounded-2xl hover:border-amber-400/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <Badge variant="outline">{(a.language || "tr").toUpperCase()}</Badge>
                        <Badge variant="outline">{a.country}</Badge>
                        <span className="text-muted-foreground">{a.count} mesaj</span>
                        <span className="text-muted-foreground">· {new Date(a.last_ts).toLocaleString()}</span>
                      </div>
                      {a.first_user_msg && (
                        <div className="mt-2">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">İlk soru</div>
                          <div className="text-sm">{a.first_user_msg}</div>
                        </div>
                      )}
                      {a.last_message && (
                        <div className="mt-2">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">Son mesaj</div>
                          <div className="text-sm text-muted-foreground italic">{a.last_message}</div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openConvo(a.session_id)}
                      data-testid={`abandoned-view-${a.session_id}`}
                    >
                      Detay
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation viewer modal */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Konuşma Detayı</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {view?.msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "text-white" : "bg-secondary"}`}
                  style={m.role === "user" ? { background: "#B39B7A" } : {}}
                >
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
